import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  Autocomplete
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { getUsageMetadata, getUsageStats, UsageRecord, UsageStatsResponse } from '../../api/admin';

type SelectOption = string;

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function SummaryCard(props: { title: string; value: string; subtitle?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 220 }}>
      <Typography variant="overline" color="text.secondary">
        {props.title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
        {props.value}
      </Typography>
      {props.subtitle && (
        <Typography variant="caption" color="text.secondary">
          {props.subtitle}
        </Typography>
      )}
    </Paper>
  );
}

function BarList(props: { title: string; rows: Array<{ label: string; value: number }>; unit?: string }) {
  const max = Math.max(1, ...props.rows.map(r => r.value));

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 300 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
        {props.title}
      </Typography>
      <Stack spacing={1}>
        {props.rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Brak danych
          </Typography>
        ) : (
          props.rows.map((row) => (
            <Box key={row.label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {row.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.value}{props.unit ? ` ${props.unit}` : ''}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={(row.value / max) * 100} />
            </Box>
          ))
        )}
      </Stack>
    </Paper>
  );
}

function LineChart(props: { title: string; points: Array<{ label: string; value: number }> }) {
  const width = 600;
  const height = 160;
  const padding = 12;

  const max = Math.max(1, ...props.points.map(p => p.value));
  const coords = props.points.map((p, idx) => {
    const x = props.points.length <= 1
      ? padding
      : padding + (idx / (props.points.length - 1)) * (width - padding * 2);
    const y = height - padding - (p.value / max) * (height - padding * 2);
    return { x, y, ...p };
  });
  const polyline = coords.map(c => `${c.x},${c.y}`).join(' ');

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 2, minWidth: 520 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
        {props.title}
      </Typography>
      {props.points.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak danych
        </Typography>
      ) : (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <svg width={width} height={height} style={{ display: 'block' }}>
            <polyline
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              points={polyline}
            />
            {coords.map((c) => (
              <circle key={c.label} cx={c.x} cy={c.y} r="3" fill="currentColor">
                <title>{c.label}: {c.value}</title>
              </circle>
            ))}
          </svg>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {props.points[0]?.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {props.points[props.points.length - 1]?.label}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

const UsageStats: React.FC = () => {
  const today = useMemo(() => new Date(), []);
  const defaultEnd = useMemo(() => toDateInputValue(today), [today]);
  const defaultStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 30);
    return toDateInputValue(d);
  }, [today]);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const [username, setUsername] = useState<SelectOption | null>(null);
  const [operationTypes, setOperationTypes] = useState<SelectOption[]>([]);
  const [modelNames, setModelNames] = useState<SelectOption[]>([]);
  const [apiProviders, setApiProviders] = useState<SelectOption[]>([]);
  const [statuses, setStatuses] = useState<SelectOption[]>([]);

  const [autoRefresh, setAutoRefresh] = useState(false);

  const [data, setData] = useState<UsageStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [metadataOpen, setMetadataOpen] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<any>(null);
  const [selectedRecord, setSelectedRecord] = useState<UsageRecord | null>(null);

  const usersOptions = data?.available_filters?.users || [];
  const operationsOptions = data?.available_filters?.operations || [];
  const modelsOptions = data?.available_filters?.models || [];
  const providersOptions = data?.available_filters?.providers || [];
  const statusesOptions = data?.available_filters?.statuses || ['success', 'failed'];

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUsageStats({
        start_date: startDate,
        end_date: endDate,
        username: username || undefined,
        operation_types: operationTypes,
        model_names: modelNames,
        api_providers: apiProviders,
        statuses,
        records_limit: 500,
        max_records: 20000
      });
      setData(response);
    } catch (err) {
      console.error('Failed to load usage stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load usage stats');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, username, operationTypes, modelNames, apiProviders, statuses]);

  useEffect(() => {
    fetchStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => {
      fetchStats();
    }, 30000);
    return () => window.clearInterval(id);
  }, [autoRefresh, fetchStats]);

  const handleRowClick = useCallback(async (record: UsageRecord) => {
    setSelectedRecord(record);
    setMetadataOpen(true);
    setMetadata(null);
    setMetadataError(null);
    setMetadataLoading(true);
    try {
      const res = await getUsageMetadata(record.username, record.generation_id);
      setMetadata(res.metadata);
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
      setMetadataError(err instanceof Error ? err.message : 'Failed to fetch metadata');
    } finally {
      setMetadataLoading(false);
    }
  }, []);

  const columns: GridColDef[] = [
    { field: 'timestamp', headerName: 'Timestamp', flex: 1.2, minWidth: 180 },
    { field: 'username', headerName: 'User', flex: 0.8, minWidth: 120 },
    { field: 'operation_type', headerName: 'Operation', flex: 1, minWidth: 160 },
    { field: 'model_name', headerName: 'Model', flex: 1, minWidth: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.value === 'failed' ? 'error' : 'success'}
        />
      )
    },
    { field: 'cost_credits', headerName: 'Credits', width: 95, type: 'number' },
    { field: 'generation_time_s', headerName: 'Time (s)', width: 95, type: 'number' },
    { field: 'api_provider', headerName: 'Provider', flex: 0.8, minWidth: 120 },
    { field: 'image_count', headerName: 'Imgs', width: 80, type: 'number' },
    { field: 'error_message', headerName: 'Error', flex: 1.5, minWidth: 220 }
  ];

  const summary = data?.stats;
  const timelinePoints = (summary?.timeline || []).map((t) => ({ label: t.date, value: t.generations }));
  const topUsers = (summary?.by_user || []).slice(0, 10).map(u => ({ label: u.username, value: u.credits }));
  const topModels = (summary?.by_model || []).slice(0, 10).map(m => ({ label: m.model_name, value: m.credits }));
  const byProvider = (summary?.by_provider || []).slice(0, 8).map(p => ({ label: p.api_provider, value: p.credits }));
  const byOperation = (summary?.by_operation || []).slice(0, 8).map(o => ({ label: o.operation_type, value: o.credits }));

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flex: 1 }}>
            <TextField
              label="Start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Stack>

          <FormControlLabel
            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
            label="Auto refresh (30s)"
          />

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchStats}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Autocomplete
            value={username}
            onChange={(_, v) => setUsername(v)}
            options={usersOptions as SelectOption[]}
            renderInput={(params) => <TextField {...params} label="Username" size="small" />}
            sx={{ flex: 1, minWidth: 220 }}
          />

          <Autocomplete
            multiple
            value={operationTypes}
            onChange={(_, v) => setOperationTypes(v)}
            options={operationsOptions as SelectOption[]}
            renderInput={(params) => <TextField {...params} label="Operations" size="small" />}
            sx={{ flex: 2, minWidth: 260 }}
          />

          <Autocomplete
            multiple
            value={modelNames}
            onChange={(_, v) => setModelNames(v)}
            options={modelsOptions as SelectOption[]}
            renderInput={(params) => <TextField {...params} label="Models" size="small" />}
            sx={{ flex: 2, minWidth: 240 }}
          />

          <Autocomplete
            multiple
            value={apiProviders}
            onChange={(_, v) => setApiProviders(v)}
            options={providersOptions as SelectOption[]}
            renderInput={(params) => <TextField {...params} label="Providers" size="small" />}
            sx={{ flex: 1.5, minWidth: 220 }}
          />

          <Autocomplete
            multiple
            value={statuses}
            onChange={(_, v) => setStatuses(v)}
            options={statusesOptions as SelectOption[]}
            renderInput={(params) => <TextField {...params} label="Status" size="small" />}
            sx={{ flex: 1, minWidth: 180 }}
          />
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <SummaryCard
          title="Total generations"
          value={summary ? `${summary.total_generations}` : '—'}
          subtitle={`Range: ${startDate} → ${endDate}`}
        />
        <SummaryCard
          title="Total credits"
          value={summary ? `${summary.total_credits}` : '—'}
        />
        <SummaryCard
          title="Failed ratio"
          value={summary ? `${Math.round((summary.failed_ratio || 0) * 1000) / 10}%` : '—'}
          subtitle={summary ? `${summary.failed_generations} failed` : undefined}
        />
        <SummaryCard
          title="Avg generation time"
          value={summary ? `${summary.avg_generation_time_s}s` : '—'}
        />
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <LineChart title="Timeline (generations/day)" points={timelinePoints} />
        <BarList title="Top users (credits)" rows={topUsers} />
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <BarList title="Top models (credits)" rows={topModels} />
        <BarList title="By operation (credits)" rows={byOperation} />
        <BarList title="By provider (credits)" rows={byProvider} />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
          Records
        </Typography>
        <Box sx={{ height: 620, width: '100%' }}>
          <DataGrid
            rows={data?.records || []}
            columns={columns}
            getRowId={(row) => (row as UsageRecord).generation_id}
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            onRowClick={(params) => handleRowClick(params.row as UsageRecord)}
            initialState={{
              sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
              pagination: { paginationModel: { pageSize: 25, page: 0 } }
            }}
            pageSizeOptions={[25, 50, 100]}
            loading={loading}
          />
        </Box>
      </Paper>

      <Dialog
        open={metadataOpen}
        onClose={() => setMetadataOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Metadata {selectedRecord ? `— ${selectedRecord.username} / ${selectedRecord.generation_id}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {metadataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : metadataError ? (
            <Alert severity="error">{metadataError}</Alert>
          ) : metadata ? (
            <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(metadata, null, 2)}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Brak danych
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetadataOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsageStats;

